import type { SubjectCode } from '~/models/Subject'
import type { Product, SupportProductName } from '~/models/Product'
import type { GradeType } from '~/models/GradeType'
import type { GradeCode } from '~/models/Grade'
import {
  findGradesByGradeType,
  grades1to4,
  grades5to7,
  grades8to10,
  gradesAdult,
  gradesPrep,
  gradesVgs
} from '~/utils/gradeSorter'
import arrayUtils from '~/utils/arrayUtils'
import { ProductVariation } from '~/models/Product'
import { useStaticProduct } from '~/composables/useStaticProduct'

export default () => {
  const { intersect } = arrayUtils()
  const { Terminprover, Biblioteket, PapirPluss } = useStaticProduct()

  const supportProductNames: SupportProductName[] = ['bokstøtte', 'lydstøtte']

  const legacyViewIsbns = [
    '9788203319778', // God i norsk 1
    '9788203319785', // God i norsk 2
    '9788203409271', // God i norsk 3
    '9788203413919', // God i Norsk på tvers
    '9788203413926', // God i norsk på tvers - modul 1
    '9788203414565', // God i norsk på tvers - modul 2
    '1122334455', // God i norsk fellesressurser
    '1122334456', // Uttale i praksis (God i norsk)
    '9788203408694', // Action
    '9788203412967', // Åpne ressurser
    '9788203319594', // Ergo fysikk 1-2
    '9788203410741', // Fokus Sosialkunnskap
    '9788203339936', // Fokus Sosiologi og sosialantropologi
    '9788203410314', // Ganas 1–2
    '9788203410482', // Geofag
    '9788203408144', // Grunnbok i historie
    '9788203408403', // Helse- og oppvekstfag
    '9788203319723', // Informasjonsteknologi 1–2
    '9788203318962', // Kjemi 1–2
    '9788203338892', // Matematikk 2P
    '9788203407673', // Matematikk for yrkesfag P
    '9788203407734', // Matematikk for yrkesfag T
    '9788203411656', // Matematikk Påbygging
    '9788203338908', // Matematikk R1
    '9788203411632', // Matematikk R2
    '9788203338915', // Matematikk S1
    '9788203411649', // Matematikk S2
    '9788203317170', // Momente 1
    '9788203317187', // Momente 2
    '9788203317156', // Naturfag for yrkesfag
    '9788203317552', // Naturfag Påbygging
    '9788203317545', // Naturfag SF
    '9788203318559', // Norsk for påbygging
    '9788203318542', // Norsk for yrkesfag
    '9788203410475', // Pasiones 1–2
    '9788203409202', // Pasos
    '9788203410093', // Psykologi 1–2
    '9788203411014', // Religion og etikk
    '9788203318979', // Scope 1–2
    '9788203339417', // Tekstbanken
    '9788203317293', // Terra Nova
  ]

  const addonProductColorMap: Record<string, string> = {
    [Biblioteket.ean]: 'purple',
    [Terminprover.ean]: 'orange',
    [PapirPluss.ean]: 'blue',
  }

  const hasRelatedLocations = (product: Product) => {
    const metadata = product.aunivers
    return !!(metadata?.relatedLocations
      && (metadata.relatedLocations.flexibleContentLocationId
        || metadata.relatedLocations.learningPathsLocationId
        || metadata.relatedLocations.interdisciplinaryContentLocationId
        || metadata.relatedLocations.teacherContentLocationId))
  }

  const productHasSubjectAndGrade = (product: Product, grade: GradeCode, subject: SubjectCode) =>
    product.subjects.includes(subject) && product.grades.includes(grade)

  const isProductVariation = (product: Product, variation: ProductVariation) =>
    product.aunivers?.productVariation === variation

  const isRelevantProduct = (product: Product, grades: GradeCode[]) => !product.grades.length || intersect(product.grades, grades).length > 0

  const isAddonProduct = (product: Product) => isProductVariation(product, ProductVariation.Addon)

  const isSupportName = (product: Required<{ name: string }>, name: SupportProductName) => product.name.toLowerCase().includes(name)

  const isSupportProduct = (product: Required<{ name: string }>) => supportProductNames.some(identifier => isSupportName(product, identifier))

  const isSearchableProduct = (product: Required<{ ean: string }>): boolean => isLibrary(product) || isExams(product)

  const isLegacyView = (product: Required<{ ean: string }>) => legacyViewIsbns.includes(product.ean)

  const isLibrary = (product: Required<{ ean: string }>) => product.ean === Biblioteket.ean
  const isPaperPlus = (product: Required<{ ean: string }>) => product.ean === PapirPluss.ean
  const isExams = (product: Required<{ ean: string }>) => product.ean === Terminprover.ean
  const isExtraProduct = (product: Required<{ ean: string }>) => isLibrary(product) || isPaperPlus(product) || isExams(product)

  const isProduct1to4 = (product: Product) =>
    intersect(product.grades, grades1to4).length > 0

  const isProduct5to7 = (product: Product) =>
    intersect(product.grades, grades5to7).length > 0

  const isProductVgs = (product: Product) =>
    intersect(product.grades, gradesVgs).length > 0

  const isProduct8to10 = (product: Product) =>
    intersect(product.grades, grades8to10).length > 0

  const isProductAdult = (product: Product) =>
    intersect(product.grades, gradesAdult).length > 0
    && ![
      91090, // Uttale i praksis
      294385, // God i norsk fellesressurser
    ].includes(product.aunivers.contentId)

  const isProductPrep = (product: Product) =>
    intersect(product.grades, gradesPrep).length > 0

  const isUnderDevelopment = (product: Product) =>
    product.aunivers.locationId === 285343

  const filterProductsByGradeType = (gradeType: GradeType) => {
    const targetGrades = findGradesByGradeType(gradeType)
    return (product: Product) => intersect(product.grades, targetGrades).length > 0
  }

  return {
    hasRelatedLocations,
    productHasSubjectAndGrade,
    isProductVariation,
    supportProductNames,
    addonProductColorMap,
    isRelevantProduct,
    isAddonProduct,
    isSupportName,
    isSupportProduct,
    isSearchableProduct,
    isLibrary,
    isPaperPlus,
    isExams,
    isExtraProduct,
    isProduct1to4,
    isProduct5to7,
    isProduct8to10,
    isProductVgs,
    isProductAdult,
    isProductPrep,
    isLegacyView,
    isUnderDevelopment,
    filterProductsByGradeType,
  }
}
